import React, { useEffect, useState } from 'react';
import OverviewBox from 'components/OverviewBox';
import ResumeVideoBox from 'components/ResumeVideoBox';

import {
	useDeleteProfileElementMutation,
	useUpdateUserDetailsMutation,
	useUserDetailsQuery,
} from 'services';
import useAuthStore, { setUserInfo } from 'store/auth';
import { RecordCard, HeaderTitle } from 'components/UserProfile';
import parse from 'html-react-parser';
import { setProfileData } from 'store/user';
import useTranslate from 'hooks/useTranslate';
import ResumeBox from 'components/ResumeBox';
import {
	calculateDateDifference,
	removeDeletedExp,
	showExpInOrder,
	sliceString,
	updateAuthValues,
} from 'utils';
import { setModalConfig } from 'store/common';
import { useNavigate, useParams } from 'react-router-dom';
import useSkeleton from 'hooks/useSkeleton';
import ProfileSkeleton from 'components/Common/Skeleton/ProfileSkeleton';
import RecentActivity from 'components/Common/RecentActivity';
import './styles.scss';

const SeekerProfile: React.FC = () => {
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const [isSelf, setIsSelf] = useState(false);
	const [delExpIndex, setDelExpIndex] = useState(0);
	const { token, user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { id: seekerId } = useParams<any>();
	const { data = {}, refetch } = useUserDetailsQuery(seekerId ?? user?.userId);
	const { mutateAsync: updateProfile } = useUpdateUserDetailsMutation();
	const { mutateAsync: deleteProfileElementMutation } = useDeleteProfileElementMutation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const delayedLoading = useSkeleton(isLoading);

	useEffect(() => {
		setIsLoading(true);
		refetch().finally(() => {
			setIsLoading(false);
		});
	}, [seekerId, user?.userId]);

	const onHideModal = () => {
		setModalConfig({
			visible: false,
			id: null,
			type: '',
			data: {
				showProfileDelIcon: data?.profilePicture.length > 0,
				showCoverDelIcon: data?.coverPicture.length > 0,
			},
		});
		refetch();
	};

	const onShowModal = (type: string, id: number | null = null) => {
		setModalConfig({
			visible: true,
			id,
			type,
			onClose: onHideModal,
			data: {
				showProfileDelIcon: data?.profilePicture.length > 0,
				showCoverDelIcon: data?.coverPicture.length > 0,
			},
		});
	};

	useEffect(() => {
		if (data?.id) {
			if ((user?.userId && !seekerId) || Number(seekerId) === user?.userId) {
				setIsSelf(true);
				setUserInfo({
					token: token,
					user: {
						...user,
						...updateAuthValues(data ?? {}),
					},
				});
			} else {
				setIsSelf(false);
			}
			setProfileData(data);
		}
	}, [data]);

	const deleteUserProfileData = (type: any, deleteKey: any, index: any) => {
		const updatedUserData = removeDeletedExp(data[deleteKey], index);
		updateProfile({ [deleteKey]: updatedUserData, saveType: type })
			.then((res: any) => {
				if (res?.success) {
					setModalConfig({
						visible: false,
						type: 'confirmModal',
						data: {
							data: {
								showProfileDelIcon: data?.profilePicture.length > 0,
								showCoverDelIcon: data?.coverPicture.length > 0,
							},
						},
						props: {
							dialogClassName: 'confirm-modal',
						},
					});
					refetch();
				}
			})
			.catch((err: any) => {});
	};

	const showDeleteModal = (type: any, deleteKey: any, id: any) => {
		setDelExpIndex(id);
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: () => deleteUserProfileData(type, deleteKey, id),
			data: {
				data: {
					showProfileDelIcon: data?.profilePicture.length > 0,
					showCoverDelIcon: data?.coverPicture.length > 0,
				},
				title: translate('Modal.wantToDelete', { title: translate(`Extras.${deleteKey}`) }),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const deleteResume = (type: any) => {
		deleteProfileElementMutation({ type: type })
			.then((res: any) => {
				if (res?.success) {
					setModalConfig({
						visible: false,
						type: 'confirmModal',
						data: {
							data: {
								showProfileDelIcon: data?.profilePicture.length > 0,
								showCoverDelIcon: data?.coverPicture.length > 0,
							},
						},
						props: {
							dialogClassName: 'confirm-modal',
						},
					});
					refetch();
				}
			})
			.catch((error) => {
				console.error('Failed to delete profile element:', error);
			});
	};

	const handleDeleteResume = (type: any) => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: () => deleteResume(type),
			data: {
				data: {
					showProfileDelIcon: data?.profilePicture.length > 0,
					showCoverDelIcon: data?.coverPicture.length > 0,
				},
				title: translate('Modal.wantToDelete', { title: `${translate('Modal.resume')}` }),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const {
		education = [],
		experience = [],
		certification = [],
		projectPublication = [],
		skills = [],
		languages = [],
	} = data;

	if (delayedLoading) {
		return <ProfileSkeleton />;
	}

	if (data?.isBlock) {
		navigate('/social-feed-posts');
	}

	return (
		<div className="right-area flex-1 d-flex flex-column">
			<OverviewBox isSelf={isSelf} data={data} onShowModal={onShowModal} receiverId={seekerId} />

			<div className="about-box white-box bg-white">
				<HeaderTitle
					title={translate('Profile.editAbout')}
					type="editAboutUs"
					onShowModal={onShowModal}
					isEdit={true}
					isSelf={isSelf}
				/>
				{data?.about && (
					<div className="about-box-content">
						<p className="mb-0">{parse(data.about.replace(/\n/g, '<br>'))}</p>
					</div>
				)}
			</div>

			<div className="experience white-box bg-white">
				<HeaderTitle
					title={translate('Profile.experience')}
					type="addExperience"
					onShowModal={onShowModal}
					isSelf={isSelf}
				/>
				{experience?.length > 0 && (
					<div className="d-flex flex-column gap-3 gap-xxl-4">
						{showExpInOrder(experience)?.map((data: any, index: number) => {
							let dateStr =
								[
									data?.startDate,
									data?.currentlyWorking === true ? translate('Common.present') : data?.endDate,
								]
									.filter(Boolean)
									.join('-') ?? '';

							dateStr += ` . ${calculateDateDifference(data?.startDate, data?.currentlyWorking ? '' : data?.endDate)}`;
							return (
								<RecordCard
									key={`exp-user-${data.employerId}-${data.jobTitle}`}
									data={{
										image: data?.employerImage ?? '',
										isImage: true,
										title: data?.jobTitle ?? '',
										subtitle: data?.employer ?? '',
										date: dateStr,
										description: data?.rolesAndResponsibility ?? '',
									}}
									id={index}
									onShowModal={onShowModal}
									showDeleteModal={showDeleteModal}
									type={'addExperience'}
									deleteKey={'experience'}
									isSelf={isSelf}
								/>
							);
						})}
					</div>
				)}
			</div>

			<div className="experience white-box bg-white">
				<HeaderTitle
					title={translate('Profile.Activity')}
					type=""
					onShowModal={onShowModal}
					isSelf={isSelf}
				/>
				<RecentActivity user={data} />
			</div>

			<div className="educations white-box bg-white">
				<HeaderTitle
					title={translate('Profile.education')}
					type="addEducation"
					onShowModal={onShowModal}
					isSelf={isSelf}
				/>
				{education?.length > 0 && (
					<div className="d-flex flex-wrap gap-3 gap-xxl-4">
						{education.map((data: any, index: number) => (
							<RecordCard
								key={`degree-${data.graduationDate}-${data.degree}`}
								data={{
									title: data?.universityName ?? '',
									subtitle: data?.degree ?? '',
									date:
										[
											data?.graduationDate,
											data?.currentlyStudying === true
												? translate('Common.present')
												: data?.graduationEndDate,
										]
											.filter(Boolean)
											.join('-') ?? '',
								}}
								id={index}
								onShowModal={onShowModal}
								showDeleteModal={showDeleteModal}
								type={'addEducation'}
								deleteKey={'education'}
								dateStyle={{ color: 'rgba(27, 27, 27, 1)' }}
								isSelf={isSelf}
							/>
						))}
					</div>
				)}
			</div>

			<div className="certifications white-box bg-white">
				<HeaderTitle
					title={translate('Profile.certification')}
					type="addCertificate"
					onShowModal={onShowModal}
					isSelf={isSelf}
				/>
				{certification?.length > 0 && (
					<div className="d-flex flex-column gap-3 gap-xxl-4">
						{certification.map((data: any, index: number) => (
							<RecordCard
								key={index}
								data={{
									image: data?.image ?? '',
									isImage: true,
									title: data?.courseName ?? '',
									subtitle: data?.instituteName ?? '',
									date: data?.completionYear ? `Issued ${data.completionYear}` : '',
								}}
								id={index}
								onShowModal={onShowModal}
								type={'addCertificate'}
								deleteKey={'certification'}
								dateStyle={{ color: 'rgba(27, 27, 27, 1)' }}
								showDeleteModal={showDeleteModal}
								isSelf={isSelf}
							/>
						))}
					</div>
				)}
			</div>

			<div className="academic white-box bg-white">
				<HeaderTitle
					title={translate('Profile.academicProject')}
					type="addAcademic"
					onShowModal={onShowModal}
					isSelf={isSelf}
				/>
				{projectPublication?.length > 0 && (
					<div className="d-flex flex-column gap-3 gap-xxl-4">
						{projectPublication.map((data: any, index: number) => {
							let dateStr =
								[
									data?.fromDate,
									data?.currentlyWorking === true ? translate('Common.present') : data?.toDate,
								]
									.filter(Boolean)
									.join('-') ?? '';

							dateStr += ` . ${calculateDateDifference(data?.fromDate, data?.currentlyWorking ? '' : data?.toDate)}`;
							return (
								<RecordCard
									key={`ppub-${data?.title}-${data?.toDate}`}
									data={{
										title: data?.title ?? '',
										subtitle: data?.noOfParticipation
											? `${data.noOfParticipation} of Participation`
											: '',
										date: dateStr,
										description: data?.description ?? '',
									}}
									id={index}
									onShowModal={onShowModal}
									type={'addAcademic'}
									deleteKey={'projectPublication'}
									showDeleteModal={showDeleteModal}
									isSelf={isSelf}
								/>
							);
						})}
					</div>
				)}
			</div>

			<div className="skills-box white-box bg-white">
				<HeaderTitle
					title={translate('Common.skill')}
					type="addSkill"
					onShowModal={onShowModal}
					isSelf={isSelf}
				/>
				{skills?.length > 0 && (
					<div className="d-flex flex-wrap gap-2 gap-sm-3 align-items-center">
						{skills.map((data: any, index: number) => (
							<div key={index} className="skills-box-name rounded-pill">
								{data?.title}
							</div>
						))}
					</div>
				)}
			</div>

			<ResumeBox
				handleDeleteResume={handleDeleteResume}
				onShowModal={onShowModal}
				isSelf={isSelf}
				type={'resume'}
			/>
			<ResumeVideoBox
				handleDeleteResume={handleDeleteResume}
				onShowModal={onShowModal}
				isSelf={isSelf}
				type={'videoResume'}
			/>

			<div className="skills-box language white-box bg-white">
				<HeaderTitle
					title={translate('Job.language')}
					type="addLanguage"
					onShowModal={onShowModal}
					isSelf={isSelf}
				/>
				{languages?.length > 0 && (
					<div className="d-flex flex-column flex-sm-row flex-wrap gap-2 gap-md-3 gap-xl-4">
						{languages.map((data: any, index: number) => (
							<RecordCard
								key={index}
								data={{
									title: data?.title ?? '',
									subtitle: data?.proficiency ?? '',
								}}
								id={index}
								onShowModal={onShowModal}
								type={'addLanguage'}
								deleteKey={'languages'}
								isSelf={isSelf}
								showDeleteModal={showDeleteModal}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default SeekerProfile;
